/**
 * Service Space (Project) Manager
 * 
 * Manages a Service Space (Project)'s Request Types (Service Request Management config item)
 * - Lists all request types for a service space (project)
 * - Add/Edit/Delete request types
 * - Track completeness status of each request type
 * - Support partial save and resume later
 * 
 * Architecture:
 * - Service Space (Project) = Config Item (e.g., "Service Request Management")
 * - Request Types = Children of Service Space (Project) (e.g., "Password Reset", "Hardware Request")
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import {
  Plus,
  Edit,
  Trash2,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Users,
  Settings,
  Copy,
  RefreshCw,
  Loader2,
  History,
  Building2,
  UserCircle,
  Lock,
} from 'lucide-react';
import { RequestTypeFormBuilder } from './RequestTypeFormBuilder';
import { projectId } from '../utils/supabase/info';
import { visibilityBadge } from '../utils/requestTypeAccess';
import { FINISHED_STATUSES, IN_PROGRESS_STATUSES, formatRelativeTime, groupByTeam, historyActionLabel, sortHistory, statusLabel, type StatusHistoryEntry } from '../utils/requestTypeHistory';
import { toast } from 'sonner';

interface RequestType {
  id: string;
  serviceProjectId: string;
  requestTypeName: string;
  description?: string;
  
  // Form Metadata
  assignmentGroup?: string;
  assignmentLogic?: string;
  approvalRequired: boolean;
  approvalLogic?: string;
  portalGroup?: string;
  notes?: string;
  
  // Field Configuration
  fields: RequestTypeField[];
  
  // Status tracking
  status: string;
  locked?: boolean;
  completenessPercentage: number; // 0-100
  statusHistory?: StatusHistoryEntry[];

  // Who can submit, the owning team (RESTORE_PLAN R1.3)
  submitterAccess?: string;
  allowedTeams?: string[];
  allowedGroups?: string[];
  allowedDepartment?: string;
  allowedIndividuals?: string[];
  allowedOrgs?: string[];
  team?: string;
  workTypeName?: string;
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

interface RequestTypeField {
  id: string;
  fieldName: string;
  fieldType: string;
  required: boolean;
  helpText?: string;
  options?: string[];
  defaultValue?: string;
  conditionalShowHide?: {
    parentFieldId: string;
    parentFieldValue: string;
  };
  hideAndPrepopulate?: {
    hidden: boolean;
    prepopulatedValue: string;
  };
  order: number;
}

interface ServiceProjectManagerProps {
  configItemId: string; // The Service Space (Project) config item ID
  companyId: string;
  sowId?: string;
  accessToken: string;
  onRequestTypesChanged?: () => void;
}

export function ServiceProjectManager({
  configItemId,
  companyId,
  sowId,
  accessToken,
  onRequestTypesChanged,
}: ServiceProjectManagerProps) {
  const [requestTypes, setRequestTypes] = useState<RequestType[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingRequestType, setEditingRequestType] = useState<RequestType | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    loadRequestTypes();
  }, [configItemId]);

  const loadRequestTypes = async () => {
    try {
      setLoading(true);
      
      // First, silently recalculate progress to fix any stale data
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/service-project/${configItemId}/recalculate-progress`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      ).catch(() => {
        // Silently fail - don't block loading if recalculation fails
      });
      
      // Then load the request types
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/service-project/${configItemId}/request-types`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setRequestTypes(data.requestTypes || []);
      } else {
        console.error('Failed to load request types');
        toast.error('Failed to load request types');
      }
    } catch (error) {
      console.error('Error loading request types:', error);
      toast.error('Error loading request types');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (requestTypeId: string) => {
    if (!confirm('Are you sure you want to delete this request type? This cannot be undone.')) {
      return;
    }

    try {
      setDeletingId(requestTypeId);
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/request-type/${requestTypeId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Request type deleted');
        loadRequestTypes();
        if (onRequestTypesChanged) onRequestTypesChanged();
      } else {
        toast.error('Failed to delete request type');
      }
    } catch (error) {
      console.error('Error deleting request type:', error);
      toast.error('Error deleting request type');
    } finally {
      setDeletingId(null);
    }
  };

  const handleDuplicate = async (requestType: RequestType) => {
    try {
      const duplicated = {
        ...requestType,
        id: undefined, // Remove ID so backend creates new one
        requestTypeName: `${requestType.requestTypeName} (Copy)`,
        status: 'draft',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/service-project/${configItemId}/request-types`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(duplicated),
        }
      );

      if (response.ok) {
        toast.success('Request type duplicated');
        loadRequestTypes();
        if (onRequestTypesChanged) onRequestTypesChanged();
      } else {
        toast.error('Failed to duplicate request type');
      }
    } catch (error) {
      console.error('Error duplicating request type:', error);
      toast.error('Error duplicating request type');
    }
  };

  const getStatusBadge = (status: RequestType['status'], locked?: boolean) => {
    const { label, tone } = statusLabel(status);
    const tones: Record<string, string> = {
      neutral: 'bg-gray-100 text-gray-800',
      info: 'bg-blue-100 text-blue-800',
      progress: 'bg-purple-100 text-purple-800',
      good: 'bg-green-100 text-green-800',
      bad: 'bg-red-100 text-red-800',
      warn: 'bg-orange-100 text-orange-800',
    };
    return (
      <Badge className={`gap-1 ${tones[tone] || tones.neutral}`} data-testid="request-type-status">
        {locked && <Lock className="w-3 h-3" />}
        {label}
      </Badge>
    );
  };

  const renderVisibility = (rt: RequestType) => {
    const badge = visibilityBadge(rt as any);
    if (!badge) return null;
    const Icon = badge.kind === 'teams' ? Users : badge.kind === 'individuals' ? UserCircle : Building2;
    return (
      <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="request-type-visibility">
        <Icon className="h-3 w-3 text-muted-foreground" />
        <span className="text-muted-foreground">{badge.label}{badge.values.length ? ':' : ''}</span>
        {badge.values.map((v) => <Badge key={v} variant="secondary">{v}</Badge>)}
      </div>
    );
  };

  const getCompletenessColor = (percentage: number) => {
    if (percentage >= 80) return 'text-green-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="py-12 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-muted-foreground" />
        <p className="mt-4 text-sm text-muted-foreground">Loading request types...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold">Request Types</h2>
          <p className="text-muted-foreground mt-1">
            Configure request types for this Service Space (Project). Each request type can have custom fields, approvals, and assignment logic.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => {
            setEditingRequestType(null);
            setShowBuilder(true);
          }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Request Type
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      {requestTypes.length > 0 && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Request Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{requestTypes.length}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {requestTypes.filter(rt => FINISHED_STATUSES.includes(rt.status)).length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">In Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {requestTypes.filter(rt => IN_PROGRESS_STATUSES.includes(rt.status)).length}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Avg Completeness</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-3xl font-bold ${getCompletenessColor(
                requestTypes.length > 0
                  ? Math.round(requestTypes.reduce((sum, rt) => sum + rt.completenessPercentage, 0) / requestTypes.length)
                  : 0
              )}`}>
                {requestTypes.length > 0
                  ? Math.round(requestTypes.reduce((sum, rt) => sum + rt.completenessPercentage, 0) / requestTypes.length)
                  : 0}%
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Request Types List */}
      {requestTypes.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Request Types Yet</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Get started by creating your first request type. Each request type represents a different form that users can submit
                (e.g., Password Reset, Hardware Request, Software Access).
              </p>
              <Button onClick={() => {
                setEditingRequestType(null);
                setShowBuilder(true);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Request Type
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {groupByTeam(requestTypes).map((group) => (
          <section key={group.team} className="space-y-4" data-testid="request-type-team-group">
            <div className="flex items-center gap-2 border-b pb-2">
              <Users className="w-4 h-4 text-muted-foreground" />
              <h3 className="font-semibold">{group.team}</h3>
              <span className="text-sm text-muted-foreground">{group.items.length} request type{group.items.length === 1 ? '' : 's'}</span>
            </div>
          {group.items.map((rt) => (
            <Card key={rt.id} className="hover:shadow-md transition-shadow" data-testid="request-type-card">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-lg">{rt.requestTypeName}</CardTitle>
                      {getStatusBadge(rt.status, rt.locked)}
                    </div>
                    {rt.description && (
                      <CardDescription>{rt.description}</CardDescription>
                    )}
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDuplicate(rt)}
                      title="Duplicate"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingRequestType(rt);
                        setShowBuilder(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(rt.id)}
                      disabled={deletingId === rt.id}
                    >
                      {deletingId === rt.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 text-destructive" />
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Completeness Progress */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Completeness</span>
                    <span className={`font-medium ${getCompletenessColor(rt.completenessPercentage)}`}>
                      {rt.completenessPercentage}%
                    </span>
                  </div>
                  <Progress value={rt.completenessPercentage} className="h-2" />
                </div>

                {/* Key Details */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground mb-1">Fields</div>
                    <div className="font-medium">{rt.fields?.length || 0} fields configured</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">Assignment</div>
                    <div className="font-medium truncate">{rt.assignmentGroup || 'Not set'}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">Approval Required</div>
                    <div className="font-medium">{rt.approvalRequired ? 'Yes' : 'No'}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground mb-1">Last Updated</div>
                    <div className="font-medium">
                      {new Date(rt.updatedAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                {(rt.fields?.length || 0) === 0 && (
                  <p className="text-sm text-muted-foreground rounded-md bg-muted/50 p-3">This form has no fields yet. Open it to add the fields customers will fill in.</p>
                )}

                {renderVisibility(rt)}

                <ChangeHistory entries={rt.statusHistory} />
              </CardContent>
            </Card>
          ))}
          </section>
          ))}
        </div>
      )}

      {/* Builder Dialog */}
      {showBuilder && (
        <Dialog open={showBuilder} onOpenChange={(open) => {
          if (!open) {
            setShowBuilder(false);
            setEditingRequestType(null);
          }
        }}>
          <DialogContent className="sm:max-w-7xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingRequestType ? 'Edit Request Type' : 'Create Request Type'}
              </DialogTitle>
              <DialogDescription>
                Define the form metadata and field configuration for this request type.
                Your progress is automatically saved.
              </DialogDescription>
            </DialogHeader>

            <RequestTypeFormBuilder
              serviceProjectId={configItemId}
              requestType={editingRequestType}
              companyId={companyId}
              sowId={sowId}
              accessToken={accessToken}
              onSave={async () => {
                setShowBuilder(false);
                setEditingRequestType(null);
                await loadRequestTypes();
                if (onRequestTypesChanged) onRequestTypesChanged();
                toast.success(editingRequestType ? 'Request type updated' : 'Request type created');
              }}
              onSaveAndAddAnother={async () => {
                setEditingRequestType(null);
                await loadRequestTypes();
                if (onRequestTypesChanged) onRequestTypesChanged();
                toast.success('Request type saved. Add another one below.');
                // Keep modal open, reset to empty form
              }}
              onCancel={() => {
                setShowBuilder(false);
                setEditingRequestType(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

/** Who moved the form to which status, when, and why; newest first, folded by default. */
function ChangeHistory({ entries }: { entries?: StatusHistoryEntry[] }) {
  const [open, setOpen] = useState(false);
  const sorted = sortHistory(entries);
  if (sorted.length === 0) return null;
  return (
    <div className="border-t pt-3" data-testid="request-type-history">
      <button type="button" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <History className="h-3.5 w-3.5" />
        {open ? 'Hide change history' : `View change history (${sorted.length})`}
      </button>
      {open && (
        <ul className="mt-2 space-y-1.5 text-sm">
          {sorted.map((e, i) => (
            <li key={`${e.at}-${i}`} className="flex flex-wrap items-baseline gap-x-2 text-muted-foreground">
              <span className="text-foreground">{historyActionLabel(e)}</span>
              <span>by {e.by?.name || 'someone'}</span>
              <span title={new Date(e.at).toLocaleString()}>{formatRelativeTime(e.at)}</span>
              {e.reason && <span className="basis-full text-xs italic">"{e.reason}"</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
